import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Modal, Form, Badge, Alert, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../styles/Cookbooks.scss";

function Cookbooks() {
  const navigate = useNavigate();

  // State management
  const [cookbooks, setCookbooks] = useState([]);
  const [filteredCookbooks, setFilteredCookbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    title: "",
    tags: "",
    notes: ""
  });
  const fileInputRef = React.useRef(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCookbook, setSelectedCookbook] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: "",
    tags: "",
    notes: ""
  });

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cookbookToDelete, setCookbookToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [allTags, setAllTags] = useState([]);

  // Fetch cookbooks on mount
  useEffect(() => {
    fetchCookbooks();
    fetchTags();
  }, []);

  // Filter cookbooks when filters change
  useEffect(() => {
    filterCookbooks();
  }, [searchQuery, selectedTag, cookbooks]);

  const fetchCookbooks = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks`,
        { credentials: "include" }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch cookbooks");
      }

      const data = await response.json();
      setCookbooks(data.cookbooks || []);
      setError("");
    } catch (err) {
      setError(err.message);
      console.error("Error fetching cookbooks:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/tags/all`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setAllTags(data.tags || []);
      }
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };

  const filterCookbooks = () => {
    let filtered = [...cookbooks];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(cookbook =>
        cookbook.title.toLowerCase().includes(query) ||
        (cookbook.notes && cookbook.notes.toLowerCase().includes(query)) ||
        (cookbook.tags && cookbook.tags.toLowerCase().includes(query))
      );
    }

    // Apply tag filter
    if (selectedTag) {
      filtered = filtered.filter(cookbook => {
        if (!cookbook.tags) return false;
        const tags = cookbook.tags.split(',').map(t => t.trim().toLowerCase());
        return tags.includes(selectedTag.toLowerCase());
      });
    }

    setFilteredCookbooks(filtered);
  };

  const handleUploadModalOpen = () => {
    setUploadFormData({ title: "", tags: "", notes: "" });
    setUploadFile(null);
    setUploadProgress(0);
    setShowUploadModal(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/epub+zip'];
      if (!validTypes.includes(file.type)) {
        setError("Invalid file type. Only PDF and EPUB files are allowed.");
        return;
      }

      // No size limit - accept any file size
      setUploadFile(file);
      setError("");

      // Auto-fill title from filename if empty
      if (!uploadFormData.title) {
        const filename = file.name.replace(/\.(pdf|epub)$/i, '');
        setUploadFormData({ ...uploadFormData, title: filename });
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadFile) {
      setError("Please select a file");
      return;
    }

    if (!uploadFormData.title.trim()) {
      setError("Please enter a title");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadFormData.title.trim());
      formData.append('tags', uploadFormData.tags.trim());
      formData.append('notes', uploadFormData.notes.trim());

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const result = JSON.parse(xhr.response);
          setSuccess("Cookbook uploaded successfully!");
          setShowUploadModal(false);
          fetchCookbooks();
          fetchTags();
          setTimeout(() => setSuccess(""), 3000);
        } else {
          const error = JSON.parse(xhr.response);
          setError(error.error || "Upload failed");
        }
        setUploading(false);
      });

      xhr.addEventListener('error', () => {
        setError("Upload failed. Please try again.");
        setUploading(false);
      });

      xhr.open('POST', `${process.env.REACT_APP_API_URL}/api/cookbooks/upload`);
      xhr.withCredentials = true;
      xhr.send(formData);

    } catch (err) {
      setError(err.message || "Upload failed");
      setUploading(false);
    }
  };

  const handleEditModalOpen = (cookbook) => {
    setSelectedCookbook(cookbook);
    setEditFormData({
      title: cookbook.title,
      tags: cookbook.tags || "",
      notes: cookbook.notes || ""
    });
    setShowEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();

    if (!editFormData.title.trim()) {
      setError("Title is required");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${selectedCookbook.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: "include",
          body: JSON.stringify({
            title: editFormData.title.trim(),
            tags: editFormData.tags.trim(),
            notes: editFormData.notes.trim()
          })
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update cookbook");
      }

      setSuccess("Cookbook updated successfully!");
      setShowEditModal(false);
      fetchCookbooks();
      fetchTags();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteModalOpen = (cookbook) => {
    setCookbookToDelete(cookbook);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!cookbookToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${cookbookToDelete.id}`,
        {
          method: 'DELETE',
          credentials: "include"
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete cookbook");
      }

      setSuccess("Cookbook deleted successfully!");
      setShowDeleteModal(false);
      setCookbookToDelete(null);
      fetchCookbooks();
      fetchTags();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenCookbook = (cookbookId) => {
    navigate(`/cookbook/${cookbookId}`);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileTypeIcon = (fileType) => {
    if (fileType === 'pdf') {
      return <i className="bi bi-file-pdf file-type-icon pdf"></i>;
    } else {
      return <i className="bi bi-book file-type-icon epub"></i>;
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="cookbooks-container mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h1>My Cookbooks</h1>
          <p className="text-muted">Your personal library of PDF and ePub cookbooks</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleUploadModalOpen}>
            <i className="bi bi-upload me-2"></i>
            Upload Cookbook
          </Button>
        </Col>
      </Row>

      {/* Alerts */}
      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}

      {/* Search and Filter */}
      <Row className="mb-4">
        <Col md={6}>
          <Form.Control
            type="text"
            placeholder="Search cookbooks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Col>
        <Col md={6}>
          <Form.Select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
          >
            <option value="">All Tags</option>
            {allTags.map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Cookbooks Grid */}
      {filteredCookbooks.length === 0 ? (
        <div className="text-center mt-5">
          <i className="bi bi-book" style={{ fontSize: '4rem', color: '#ccc' }}></i>
          <p className="text-muted mt-3">
            {cookbooks.length === 0
              ? "No cookbooks yet. Upload your first cookbook to get started!"
              : "No cookbooks match your search criteria."}
          </p>
        </div>
      ) : (
        <Row>
          {filteredCookbooks.map(cookbook => (
            <Col md={4} lg={3} key={cookbook.id} className="mb-4">
              <Card className="cookbook-card h-100">
                <Card.Body>
                  <div className="d-flex align-items-start mb-3">
                    {getFileTypeIcon(cookbook.file_type)}
                    <div className="ms-2 flex-grow-1">
                      <Card.Title className="mb-1" style={{ fontSize: '1rem' }}>
                        {cookbook.title}
                      </Card.Title>
                      <small className="text-muted">
                        {formatFileSize(cookbook.file_size)}
                      </small>
                    </div>
                  </div>

                  {cookbook.tags && (
                    <div className="mb-2">
                      {cookbook.tags.split(',').map((tag, idx) => (
                        <Badge key={idx} bg="secondary" className="me-1 mb-1">
                          {tag.trim()}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {cookbook.notes && (
                    <p className="text-muted small mb-2" style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {cookbook.notes}
                    </p>
                  )}

                  <div className="d-grid gap-2 mt-3">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleOpenCookbook(cookbook.id)}
                    >
                      <i className="bi bi-book-open me-1"></i> Open
                    </Button>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="flex-grow-1"
                        onClick={() => handleEditModalOpen(cookbook)}
                      >
                        <i className="bi bi-pencil"></i> Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="flex-grow-1"
                        onClick={() => handleDeleteModalOpen(cookbook)}
                      >
                        <i className="bi bi-trash"></i> Delete
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={() => !uploading && setShowUploadModal(false)} size="lg">
        <Modal.Header closeButton={!uploading}>
          <Modal.Title>Upload Cookbook</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpload}>
            <Form.Group className="mb-3">
              <Form.Label>Select File *</Form.Label>
              <div className="d-grid gap-2">
                <Button
                  variant="outline-primary"
                  size="lg"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  type="button"
                >
                  <i className="bi bi-file-earmark-arrow-up me-2"></i>
                  {uploadFile ? 'Change File' : 'Choose File from Computer'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.epub,application/pdf,application/epub+zip"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  style={{ display: 'none' }}
                  required
                />
              </div>
              <Form.Text className="text-muted">
                Accepted formats: PDF, ePub (unlimited file size)
              </Form.Text>
            </Form.Group>

            {uploadFile && (
              <Alert variant="success">
                <i className="bi bi-check-circle me-2"></i>
                <strong>Selected:</strong> {uploadFile.name} ({formatFileSize(uploadFile.size)})
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                value={uploadFormData.title}
                onChange={(e) => setUploadFormData({ ...uploadFormData, title: e.target.value })}
                disabled={uploading}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Italian, Baking, Quick Meals"
                value={uploadFormData.tags}
                onChange={(e) => setUploadFormData({ ...uploadFormData, tags: e.target.value })}
                disabled={uploading}
              />
              <Form.Text className="text-muted">
                Separate multiple tags with commas
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Personal notes about this cookbook..."
                value={uploadFormData.notes}
                onChange={(e) => setUploadFormData({ ...uploadFormData, notes: e.target.value })}
                disabled={uploading}
              />
            </Form.Group>

            {uploading && (
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="progress">
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${uploadProgress}%` }}
                    aria-valuenow={uploadProgress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpload} disabled={uploading || !uploadFile}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Cookbook</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEdit}>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Italian, Baking, Quick Meals"
                value={editFormData.tags}
                onChange={(e) => setEditFormData({ ...editFormData, tags: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEdit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)}>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Delete Cookbook</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cookbookToDelete && (
            <p>
              Are you sure you want to delete <strong>{cookbookToDelete.title}</strong>?
              This action cannot be undone.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Cookbooks;
